// @flow

import React, {useMemo, useState} from 'react';
import {makeStyles, Typography, Box, Divider} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import {Switch, Route, Link} from 'react-router-dom';

import igIcon from '../../../../../assets/img/chat/ig.png';
import wsspIcon from '../../../../../assets/img/chat/wssp.png';
import wsspBussinessIcon from '../../../../../assets/img/chat/wssp-api.webp';
import messengerIcon from '../../../../../assets/img/chat/messenger.png';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import WhatsappWebConfig from './SocialConfig/whatsapp-web-config';

import type {Node} from 'react';
import {
  useFeatureSpec,
  useIsFeatureInTier,
} from '../../../../../utils/component';
import TierRestrictionModal from '../../../../../elements/TierRestrictionModal/TierRestrictionModal';
import MetaConfig from './SocialConfig/meta-config';
import CustomChip from '../../../../../elements/CustomChip';
import {useStoreDevices} from '../../utils';
import {useHistory} from 'react-router-dom';
import {classNames} from '../../../../../utils/react/className';

type Props = {};

type ChannelQuota = {
  limit: number,
  remaining: number,
};

type StepContent = {
  component: Node,
  disabled: boolean,
  messageDisabled?: string,
  icon: string,
  name: string,
  path: string,
  description?: string,
  accountType?: 'personal' | 'business',
  section?: 'personal' | 'business',
  documentationUrl?: string,
  quota?: ChannelQuota,
};

function getChannelQuota(spec: ?Object, connectedCount: number): ?ChannelQuota {
  if (!spec || spec.limit == null || spec.limit === '') return null;

  const limit = Number(spec.limit);
  if (!Number.isFinite(limit) || limit <= 0) return null;

  return {
    limit,
    remaining: Math.max(0, limit - connectedCount),
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    margin: '1rem 0',
    backgroundColor: '#FFFFFF',
    borderRadius: '.875rem',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08)',
    maxWidth: '1200px',
  },
  title: {
    fontSize: 18,
    color: colors.grey900,
    fontWeight: 600,
    textAlign: 'center',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialList: {
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  socialItem: {
    borderRadius: 8,
    border: `1px solid ${colors.grey400}`,
    backgroundColor: '#FFFFFF',
    position: 'relative',

    '&:hover .comingSoonChip': {
      opacity: 1,
    },

    '& .comingSoonChip': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: '#ffffff40',
      backdropFilter: 'blur(.125rem)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity .3s ease',
    },
  },
  selected: {
    border: '1px solid #7B39FF',
  },
  disabled: {
    opacity: 0.2,
  },
  icon: {
    height: 26,
    width: 26,
    marginRight: theme.spacing(1),
  },
  continueButton: {
    fontSize: 16,
    fontWeight: 600,
    padding: theme.spacing(1, 0),
  },
  selectSocialNetwork: {
    margin: '1rem 2rem',
    maxWidth: 600,
    width: '100%',
  },
  itemButton: {
    borderRadius: 8,
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: '.5rem',
    display: 'flex',
    alignItems: 'center',

    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  firstStep: {
    maxWidth: '100%',
    maxHeight: '100%',
    '& img': {
      with: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    },
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionDescription: {
    fontSize: 14,
    color: colors.grey600,
    fontWeight: 400,
  },
  accountTypeBadge: {
    fontSize: 11,
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  personalAccount: {
    backgroundColor: '#E8F5E8',
    color: '#2E7D32',
  },
  businessAccount: {
    backgroundColor: '#FFF3E0',
    color: '#F57C00',
  },
  platformDescription: {
    fontSize: 13,
    color: colors.grey600,
    whiteSpace: 'pre-line',
  },
  quotaText: {
    fontSize: 12,
    fontWeight: 500,
    color: colors.grey600,
    marginTop: 4,
  },
  quotaAvailable: {
    color: '#2E7D32',
  },
  quotaEmpty: {
    color: '#F57C00',
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: colors.grey300,
  },
}));

export default function Configuration(props: Props) {
  const classes = useStyles();
  const history = useHistory();

  const queryString = history.location.search;
  const params = new URLSearchParams(queryString);
  const phoneReconnect = params.get('identifier');

  const hasTiendadaChatWhatsappFeature = useIsFeatureInTier(
    'TIENDACHAT_WHATSAPP_WEB'
  );
  const hasTiendadaChatInstagramFeature = useIsFeatureInTier(
    'TIENDACHAT_INSTAGRAM'
  );
  const hasTiendadaChatMessengerFeature = useIsFeatureInTier(
    'TIENDACHAT_FB_MESSENGER'
  );
  const hasWhatsappApiFeature = useIsFeatureInTier('TIENDACHAT_WHATSAPP_API');

  const whatsappWebSpec = useFeatureSpec('TIENDACHAT_WHATSAPP_WEB');
  const instagramSpec = useFeatureSpec('TIENDACHAT_INSTAGRAM');
  const messengerSpec = useFeatureSpec('TIENDACHAT_FB_MESSENGER');
  const whatsappApiSpec = useFeatureSpec('TIENDACHAT_WHATSAPP_API');

  const {storeDevices} = useStoreDevices({
    refetchOnMount: true,
  });

  const identifierInStoreDevices = storeDevices.find(
    device => device.identifier === phoneReconnect
  );

  const storeDevicesWhatsappLength = storeDevices.filter(
    device => device.service === 'WHATSAPP_WEB' && device.state === 'CONNECTED'
  ).length;
  const storeDevicesInstagramLength = storeDevices.filter(
    device => device.service === 'INSTAGRAM' && device.state === 'CONNECTED'
  ).length;
  const storeDevicesMessengerLength = storeDevices.filter(
    device => device.service === 'FB_MESSENGER' && device.state === 'CONNECTED'
  ).length;
  const storeDevicesWhatsappApiLength = storeDevices.filter(
    device => device.service === 'WHATSAPP_API' && device.state === 'CONNECTED'
  ).length;

  const whatsappWebQuota = getChannelQuota(
    whatsappWebSpec,
    storeDevicesWhatsappLength
  );
  const instagramQuota = getChannelQuota(
    instagramSpec,
    storeDevicesInstagramLength
  );
  const messengerQuota = getChannelQuota(
    messengerSpec,
    storeDevicesMessengerLength
  );
  const whatsappApiQuota = getChannelQuota(
    whatsappApiSpec,
    storeDevicesWhatsappApiLength
  );

  const whatsappWebAtLimit =
    !!whatsappWebQuota && whatsappWebQuota.remaining === 0;
  const instagramAtLimit = !!instagramQuota && instagramQuota.remaining === 0;
  const messengerAtLimit = !!messengerQuota && messengerQuota.remaining === 0;
  const whatsappApiAtLimit =
    !!whatsappApiQuota && whatsappApiQuota.remaining === 0;

  const [tierModalOpen, setTierModalOpen] = useState(false);

  const socialItemsAvailable: StepContent[] = useMemo(() => {
    return [
      {
        name: 'WhatsApp Web',
        icon: wsspIcon,
        disabled:
          !hasTiendadaChatWhatsappFeature ||
          (whatsappWebAtLimit && !identifierInStoreDevices),
        messageDisabled: hasTiendadaChatWhatsappFeature
          ? 'Ya tienes configurado WhatsApp'
          : 'Mejora tu plan',
        component: <WhatsappWebConfig />,
        path: 'whatsapp',
        description: 'Conecta tu WhatsApp personal como WhatsApp Web',
        accountType: 'personal',
        section: 'personal',
        quota: hasTiendadaChatWhatsappFeature ? whatsappWebQuota : undefined,
      },
      {
        name: 'Instagram Business',
        icon: igIcon,
        disabled: !hasTiendadaChatInstagramFeature || instagramAtLimit,
        messageDisabled: hasTiendadaChatInstagramFeature
          ? 'Ya tienes configurado Instagram'
          : 'Mejora tu plan',
        component: <MetaConfig type="ig" />,
        path: 'instagram',
        description:
          'Requiere cuenta de Instagram Business conectada a Facebook',
        accountType: 'business',
        section: 'business',
        documentationUrl: 'https://business.instagram.com/getting-started',
        quota: hasTiendadaChatInstagramFeature ? instagramQuota : undefined,
      },
      {
        name: 'Facebook Messenger',
        icon: messengerIcon,
        disabled: !hasTiendadaChatMessengerFeature || messengerAtLimit,
        messageDisabled: hasTiendadaChatMessengerFeature
          ? 'Ya tienes configurado Messenger'
          : 'Mejora tu plan',
        component: <MetaConfig type="fb" />,
        path: 'messenger',
        description: 'Requiere página de Facebook Business',
        accountType: 'business',
        section: 'business',
        documentationUrl: 'https://business.facebook.com/',
        quota: hasTiendadaChatMessengerFeature ? messengerQuota : undefined,
      },
      {
        name: 'WhatsApp Business API',
        icon: wsspBussinessIcon,
        path: 'whatsapp-api',
        disabled: !hasWhatsappApiFeature || whatsappApiAtLimit,
        messageDisabled: hasWhatsappApiFeature
          ? 'Ya hay un dispositivo conectado, Eliminalo para conectar uno nuevo'
          : 'Mejora tu plan',
        component: <MetaConfig type="wb" />,
        description: 'Requiere WhatsApp Business API y verificación de Meta',
        accountType: 'business',
        section: 'business',
        documentationUrl:
          'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started/',
        quota: hasWhatsappApiFeature ? whatsappApiQuota : undefined,
      },
    ];
  }, [
    hasTiendadaChatInstagramFeature,
    hasTiendadaChatMessengerFeature,
    hasTiendadaChatWhatsappFeature,
    hasWhatsappApiFeature,
    identifierInStoreDevices,
    instagramAtLimit,
    instagramQuota,
    messengerAtLimit,
    messengerQuota,
    whatsappApiAtLimit,
    whatsappApiQuota,
    whatsappWebAtLimit,
    whatsappWebQuota,
  ]);

  const personalPlatforms = socialItemsAvailable.filter(
    item => item.section === 'personal'
  );
  const businessPlatforms = socialItemsAvailable.filter(
    item => item.section === 'business'
  );

  return (
    <div className={classes.root}>
      <TierRestrictionModal
        open={tierModalOpen}
        handleClose={() => {
          setTierModalOpen(false);
        }}
      />
      <Switch>
        <Route exact path="/chat/configuration">
          <div className={classes.center}>
            <div className={classes.selectSocialNetwork}>
              <p className={classes.title}>
                Selecciona la plataforma que deseas conectar
              </p>

              {/* Personal Section */}
              {personalPlatforms.length > 0 && (
                <Box mb={2}>
                  <Typography className={classes.sectionTitle}>
                    Cuenta Personal
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    Usa tu cuenta personal existente. No necesitas cuentas
                    business.
                  </Typography>
                  <ul className={classes.socialList}>
                    {personalPlatforms.map(el => (
                      <PlatformItem key={el.path} platform={el} />
                    ))}
                  </ul>
                </Box>
              )}

              {/* Divider */}
              {personalPlatforms.length > 0 && businessPlatforms.length > 0 && (
                <Divider className={classes.divider} />
              )}

              {/* Business Section */}
              {businessPlatforms.length > 0 && (
                <Box>
                  <Typography className={classes.sectionTitle}>
                    Cuentas Business
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    Requieren cuentas business verificadas y configuración
                    adicional.
                  </Typography>
                  <ul className={classes.socialList}>
                    {businessPlatforms.map(el => (
                      <PlatformItem key={el.path} platform={el} />
                    ))}
                  </ul>
                </Box>
              )}
            </div>
          </div>
        </Route>
        {socialItemsAvailable
          .filter(el => el.component && !el.disabled)
          .map((route, idx) => (
            <Route
              exact
              key={route.name}
              path={`/chat/configuration/${route.path}`}
            >
              {route.component}
            </Route>
          ))}
      </Switch>
    </div>
  );
}

function PlatformItem({platform}) {
  const classes = useStyles();

  return (
    <li key={platform.name} className={classes.socialItem}>
      <CustomButton
        fullWidth
        className={classNames(classes.itemButton, {
          [classes.disabled]: platform.disabled,
        })}
        to={`/chat/configuration/${platform.path}`}
        component={Link}
        disabled={platform.disabled}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            gap: '12px',
          }}
        >
          {platform.icon && (
            <img
              src={platform.icon}
              alt={`${platform.name} icon`}
              className={classes.icon}
              style={{marginTop: '2px', marginRight: 0}}
            />
          )}
          <div style={{flex: 1, textAlign: 'left'}}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
                gap: '8px',
              }}
            >
              <Typography
                style={{
                  fontWeight: 600,
                  fontSize: '.875rem',
                  color: colors.grey900,
                }}
              >
                {platform.name}
              </Typography>
              {platform.accountType && (
                <span
                  className={classNames(classes.accountTypeBadge, {
                    [classes.personalAccount]:
                      platform.accountType === 'personal',
                    [classes.businessAccount]:
                      platform.accountType === 'business',
                  })}
                >
                  {platform.accountType === 'personal'
                    ? 'Personal'
                    : 'Business'}
                </span>
              )}
            </div>
            {platform.description && (
              <Typography className={classes.platformDescription}>
                {platform.description}
              </Typography>
            )}
            {platform.quota && (
              <Typography
                className={classNames(classes.quotaText, {
                  [classes.quotaAvailable]: platform.quota.remaining > 0,
                  [classes.quotaEmpty]: platform.quota.remaining === 0,
                })}
              >
                {platform.quota.remaining} de {platform.quota.limit}{' '}
                {platform.quota.remaining === 1 ? 'disponible' : 'disponibles'}
              </Typography>
            )}
            {platform.documentationUrl && (
              <div style={{marginTop: '8px'}}>
                <CustomButton
                  size="small"
                  variant="text"
                  component="a"
                  href={platform.documentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  style={{
                    fontSize: '12px',
                    padding: '2px 6px',
                    minHeight: 'auto',
                    textTransform: 'none',
                  }}
                >
                  Ver guía oficial
                </CustomButton>
              </div>
            )}
          </div>
        </div>
      </CustomButton>
      {platform.disabled && (
        <span className="comingSoonChip">
          <CustomChip
            label={platform.messageDisabled || 'Muy pronto'}
            size="small"
            color="primary"
          />
        </span>
      )}
    </li>
  );
}
